import * as PropTypes from 'prop-types';
import {ClientMode, EditorLayersMode, EditorTool, FeatureZoom, KeyboardShortcut} from '../../enums';
import {Color, EditorToolDefinition, ExtendedEditorToolDefinition, FeatureDeleteRequest, StyleDefinition} from '../../types';
import {Feature} from 'geojson';
import {FeatureZoomOptions} from '../../actions/FeatureActions';

/**
 * Properties specifické pro mapový editor.
 */
export interface EditorProps {
    /**
     * Příznak, zda je editor v read-only režimu.
     * V tomto režimu jsou dostupné pouze nástroje neumožňující modifikaci features.
     * Výchozí hodnota: `false`
     */
    readOnly?: boolean;

    /**
     * Režim editačních vrstev.
     * Výchozí hodnota: {@link EditorLayersMode.OVERLAY}.
     */
    layersMode?: EditorLayersMode;

    /**
     * Definice stylů pro vykreslení editovaných features.
     *
     * Pouze pro režim editačních vrstev {@link EditorLayersMode.OVERLAY}.
     * Feature se na daný styl musí odkazovat pomocí své property `styleId`
     * nebo `selectStyleId`. V opačném případě bude pro vykreslení feature
     * použita základní barva {@link color}, či případně styl nástroje určený
     * dle feature property `toolId`.
     */
    styles?: StyleDefinition[];

    /**
     * Základní barva editovaných features.
     * Pouze pro režim editačních vrstev {@link EditorLayersMode.OVERLAY}.
     * Výchozí hodnota: Barva definovaná na straně Mapio.
     */
    color?: Color;

    /**
     * Šířka čáry editovaných features.
     * Pouze pro režim editačních vrstev {@link EditorLayersMode.OVERLAY}.
     * Výchozí hodnota: Šířka definovaná na straně Mapio.
     */
    lineWidth?: number;

    /**
     * Nástroje dostupné v toolbaru nebo jako plovoucí tlačítka nad mapou.
     */
    tools?: (EditorTool | EditorToolDefinition | ExtendedEditorToolDefinition)[];

    /**
     * Nástroje dostupné skrze kontextové menu myši.
     */
    contextTools?: (EditorTool | EditorToolDefinition)[];

    /**
     * Povolené klávesové zkratky.
     * Výchozí hodnota: `[KeyboardShortcut.DELETE]`.
     */
    keyboardShortcuts?: KeyboardShortcut[];

    /**
     * Callback volaný při požadavku na smazání features z editoru uživatelem.
     *
     * Pokud je callback definován, nebude zobrazeno stanradní modální potvrzovací
     * okno, ale dojde místo toho k vyvolání předaného callbacku. Programátor má
     * má tak možnost zobrazit vlastní imlementaci potvrzení uživatele a následně
     * pomocí akce {@link FeatureActions.deleteFeatures} dané features skutečně
     * smazat.
     *
     * @param request - Požadavek na smazání features obsahující jejich identifikaci.
     */
    featureDeleteHandler?: (request: FeatureDeleteRequest) => void;

    /**
     * Příznak, zda zobrazovat editované features při tisku.
     * Výchozí hodnota: `false`.
     */
    featurePrintEnabled?: boolean;

    /**
     * Výchozí features jež se mají načíst pro editaci.
     * Pouze pro režim editačních vrstev {@link EditorLayersMode.OVERLAY}.
     */
    initialFeatures?: Feature[];

    /**
     * Parametry pro načtení {@link initialFeatures}.
     */
    initialFeaturesOptions?: FeatureZoomOptions;

    /**
     * Příznak, zda do tisku tisku zahrnout vrstvy vybrané uživatelem.
     * Výchozí hodnota: `false`.
     * @deprecated Použijte {@link MapProps.rasterLayersPrintMode} a {@link MapProps.vectorLayersPrintMode}.
     */
    printSelectedLayers?: boolean;

    /**
     * Příznak, zda do tisku tisku zahrnout {@link initialFeatures},
     * respektive obsah editační vrstvy při režimu {@link EditorLayersMode.OVERLAY}.
     * Výchozí hodnota: `false`.
     * @deprecated Použijte {@link featurePrintEnabled}.
     */
    printInitialFeatures?: boolean;

    /**
     * Režim zobrazení, určující URL pro zobrazení v mapové komponentě.
     * Výchozí hodnota: {@link ClientMode.EMBED_EDITOR}
     */
    clientMode?: ClientMode;

    /**
     * Množina vzorů ID vrstev, omezující které vrstvy se mají nabízet pro editaci v editačním režimu.
     * Pokud vzor končí '*', považuje se za prefix. V opačném případě se porovnává na přesnou shodu.
     * Pokud je null nebo prázdné pole, zobrazují se všechny vrstvy.
     * Má význam pouze pro režim {@link EditorLayersMode.DATA}.
     */
    editableLayerIds?: string[];
}

/**
 * Typy properties specifických pro mapový editor.
 *
 * @ignore
 */
export const editorPropTypes = {
    readOnly: PropTypes.bool,
    layersMode: PropTypes.oneOf(Object.values(EditorLayersMode)),
    styles: PropTypes.array,
    color: PropTypes.arrayOf(PropTypes.number),
    lineWidth: PropTypes.number,
    tools: PropTypes.array,
    contextTools: PropTypes.array,
    keyboardShortcuts: PropTypes.array,
    featureDeleteHandler: PropTypes.func,
    featurePrintEnabled: PropTypes.bool,
    initialFeatures: PropTypes.array,
    initialFeaturesOptions: PropTypes.shape({
        zoom: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(Object.values(FeatureZoom))]),
        zoomPadding: PropTypes.arrayOf(PropTypes.number),
        zoomRatio: PropTypes.number,
        zoomMinScale: PropTypes.number,
    }),
    printSelectedLayers: PropTypes.bool,
    printInitialFeatures: PropTypes.bool,
    clientMode: PropTypes.oneOf(Object.values(ClientMode)),
    editableLayerIds: PropTypes.array,
};

/**
 * Výchozí hodnoty properties specifických pro mapový editor.
 *
 * @ignore
 */
export const editorDefaultProps = {
    // Zcela záměrně neuvádíme default hodnoty properties, které se synchronizují s mapio.
    // 1) Jsou definovány na straně mapio. Neduplikujeme tím pádem logiku
    //    a neposíláme zbytečné zprávy přes frame.
    // 2) V rámci zpětné kompatibility udržujeme některé deprecated properties.
    //    Default ostatních properties by je mohl přepsat (pokud by šlo o alias).
};
